/* iter-72f — Trial → Pro upgrade dialog.
 *
 * Appears once per browser session for any user whose
 * `subscription_tier` is `trial`. Reuses the same pricing catalog the
 * admin exposes (starter / pro / power) so the benefits list stays in
 * sync with what the customer actually gets after upgrading.
 *
 * Behaviour:
 *   - Skipped when `useAuth` is still resolving (`checking === true`).
 *   - Skipped when the user is not on a trial.
 *   - Only shows ONCE per browser session (tracked via sessionStorage
 *     under `pw:trial-upgrade-shown`). Cleared on sign-out so the next
 *     login triggers it again — matching the "each time the 14-day
 *     user logs in" requirement without hammering them on every page
 *     navigation within a single session.
 *   - "Maybe later" closes without dismissing forever — they'll see
 *     it next login. "Upgrade now" opens the plans dialog / navigates
 *     to /pricing.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Clock } from 'lucide-react';
import { useAuth } from '@/lib/auth_context';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'pw:trial-upgrade-shown';
const DAILY_KEY   = 'pw:trial-upgrade-shown-day';   // holds YYYY-MM-DD
const URGENT_DAY_THRESHOLD = 3;

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Fallback benefits when we can't fetch the live plans catalog. */
const FALLBACK_BENEFITS = [
  'Unlimited chapter drafts (Trial: 3 chapters)',
  'Character Bible + inline suggestions',
  'Adobe PDF + KDP bundle exports',
  'Priority Claude Sonnet 4.5 generations',
  'Community forums + real-time chat dock',
];

function daysLeft(trialExpiresAt) {
  if (!trialExpiresAt) return null;
  const ms = new Date(trialExpiresAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 0;
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
}

export default function TrialUpgradeDialog() {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [benefits, setBenefits] = useState(FALLBACK_BENEFITS);

  useEffect(() => {
    if (checking) return;
    if (!user) return;
    if (user.subscription_tier !== 'trial') return;
    // iter-72h — the TrialExpiredScreen takes over the /app route once
    // the trial window has closed, so skip the incremental upsell.
    if (user.trial_expired) return;
    if (typeof window === 'undefined') return;

    /* iter-72g — Nag cadence changes as the trial winds down:
     *  • > 3 days left → once per browser session (SESSION_KEY, cleared on logout).
     *  • ≤ 3 days left → once per calendar day (DAILY_KEY holds YYYY-MM-DD).
     * Either marker is honoured until the user actually engages
     * (see markShown() below). */
    const remaining = daysLeft(user.trial_expires_at);
    const urgent = remaining !== null && remaining <= URGENT_DAY_THRESHOLD;
    try {
      if (urgent) {
        const lastDay = window.localStorage.getItem(DAILY_KEY);
        if (lastDay === todayIso()) return;
      } else if (window.sessionStorage.getItem(SESSION_KEY) === '1') {
        return;
      }
    } catch (_e) { /* ignore — private mode */ }

    /* iter-72f — poll until any onboarding overlays have unmounted
     * (project WelcomeOverlay uses Radix `[role=dialog]` with
     * data-testid=welcome-overlay). Only then reveal the trial upsell
     * so it's not obscured — and only record the "shown" marker when
     * the user actually engages (see markShown()), so a covered dialog
     * doesn't silently burn the session. */
    let cancelled = false;
    const tryOpen = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined'
          && document.querySelector('[data-testid=welcome-overlay]')) {
        window.setTimeout(tryOpen, 600);
        return;
      }
      setOpen(true);
    };
    window.setTimeout(tryOpen, 400);
    return () => { cancelled = true; };
  }, [checking, user]);

  /* iter-72g — Banner-initiated open bypasses BOTH markers so the
   * user can always summon the dialog by clicking the trial banner
   * upgrade button. */
  useEffect(() => {
    if (!user || user.subscription_tier !== 'trial') return undefined;
    const onOpen = () => setOpen(true);
    window.addEventListener('pw:trial-upgrade-open', onOpen);
    return () => window.removeEventListener('pw:trial-upgrade-open', onOpen);
  }, [user]);

  const markShown = () => {
    if (!user) return;
    const remaining = daysLeft(user.trial_expires_at);
    const urgent = remaining !== null && remaining <= URGENT_DAY_THRESHOLD;
    try {
      if (urgent) {
        window.localStorage.setItem(DAILY_KEY, todayIso());
      } else {
        window.sessionStorage.setItem(SESSION_KEY, '1');
      }
    } catch (_e) { /* private mode */ }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/subscriptions/plans`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const pro = Array.isArray(r.data?.plans)
          ? r.data.plans.find((p) => p.id === 'pro' || p.id === 'professional' || p.name?.toLowerCase().includes('pro'))
          : null;
        if (pro && Array.isArray(pro.features) && pro.features.length > 0) {
          setBenefits(pro.features);
        }
      })
      .catch(() => { /* keep the fallback list */ });
    return () => { cancelled = true; };
  }, [open]);

  if (!open || !user) return null;
  const remaining = daysLeft(user.trial_expires_at);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { markShown(); setOpen(false); } }}>
      <DialogContent className="z-[60] max-w-lg" data-testid="trial-upgrade-dialog">
        <DialogHeader>
          <div className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full bg-amber-500/15 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.24em] text-amber-700 dark:text-amber-300">
            <Clock className="h-3 w-3" />
            {remaining === null
              ? '14-day trial'
              : remaining === 0
                ? 'Trial expired'
                : `${remaining} day${remaining === 1 ? '' : 's'} left in your trial`}
          </div>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-primary" />
            Ready to graduate to The Loom?
          </DialogTitle>
          <DialogDescription>
            You&rsquo;re on the free 14-day trial. Upgrading now locks in the
            full Pro palette so your novel keeps moving after the trial ends.
          </DialogDescription>
        </DialogHeader>

        <ul className="my-2 space-y-1.5" data-testid="trial-upgrade-benefits">
          {benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px]">
              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <DialogFooter className="flex flex-row gap-2 sm:justify-end">
          <Button
            variant="outline"
            data-testid="trial-upgrade-later"
            onClick={() => { markShown(); setOpen(false); }}
          >
            Maybe later
          </Button>
          <Button
            data-testid="trial-upgrade-cta"
            onClick={() => {
              markShown();
              setOpen(false);
              navigate('/app?view=billing');
            }}
          >
            <Sparkles className="mr-1.5 h-4 w-4" />
            Upgrade now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
